import {
  ApplicationConfig,
  importProvidersFrom,
  ErrorHandler,
  isDevMode,
  inject,
  provideAppInitializer,
} from '@angular/core';
import { ENV } from '@bodyanalytics/app-config';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withFetch,
  withInterceptors,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { provideAuth0, AuthService, HttpInterceptorRouteConfig } from '@auth0/auth0-angular';
import { EmbeddedBridgedAuthService, embeddedAwareAuthHttpInterceptorFn } from '@bodyanalytics/authentication-ui';
import { provideRouter, withComponentInputBinding, withInMemoryScrolling } from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideStore } from '@ngrx/store';
import { provideRouterStore } from '@ngrx/router-store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { NgxEchartsModule } from 'ngx-echarts';
import { ToastrModule } from 'ngx-toastr';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialogModule } from '@angular/material/dialog';
import {
  ApiLocalRoutesEnum,
  TenantInterceptor,
  GlobalErrorHandler,
  // Promoted to ui-core so the calendar portal shares the same runtime-theming
  // implementation (init by tenantId here; initBySlug for `/t/:slug` portals).
  BrandingService,
  httpErrorInterceptor,
  ngZoneReentryInterceptor,
  provideSentryErrorReporting,
  withMaintenanceMode,
} from '@bodyanalytics/ui-core';
import { MAT_TOOLTIP_DEFAULT_OPTIONS } from '@angular/material/tooltip';

import appRoutes from './app.routes';
import { environment } from '../environments/environment';

/**
 * API module prefixes (first path segment after the API base) the admin app
 * actually calls, enumerated from every HTTP call site in
 * `apps/front/admin/src` + `libs/front/admin/**` + the shared-lib services
 * admin injects (`BaseHttpCrudService` subclasses, `AdminAuthService`).
 *
 * #2061 — replaces the blanket `allowedList: ['${api}/*']`. A blanket list
 * attaches the Auth0 interceptor to EVERY API call; any public/pre-auth
 * endpoint added later silently breaks for anonymous visitors, because a
 * failed silent token fetch on a bare-string entry throws before the request
 * hits the network (the #2056 customer-registration failure mode). The admin
 * app is fully authenticated today — every route below is behind the Auth0
 * AuthGuard client-side and AuthorizationGuard server-side (including
 * `admin-register-api`: its front route uses `canActivate: [AuthGuard]` and
 * its controller uses `AuthorizationGuard` + `PermissionsGuard`) — so scoping
 * to these module prefixes changes no current behavior while keeping
 * customer-facing public modules (`registration-api`, `events-api`, ...) and
 * any future module off the token-required set by default.
 *
 * The pre-login APP_INITIALIZER branding fetch
 * (`branding.service.ts` → `GET ${api}/tenants/:id/branding`) uses the native
 * `fetch` API, deliberately bypassing this interceptor, so it needs no entry;
 * `tenants` is listed for the authenticated branding-management page, which
 * hits the same path via `HttpClient`.
 *
 * When a NEW backend module is wired into the admin app, add its prefix here
 * (a missing prefix means no bearer token → 401 from the API), and when a
 * sub-route under a listed prefix is made `@Public()` server-side, add a
 * matching `allowAnonymous` entry to ADMIN_PUBLIC_API_ROUTES below —
 * otherwise the module wildcard swallows it into the token-required set and
 * a failed silent token fetch kills the request (#2056 failure mode).
 *
 * Prefix strings are derived from `ApiLocalRoutesEnum` (the same enum admin
 * services build their URLs from) so list and call sites cannot drift.
 * #2108 — added the four bare-prefix members this list was missing
 * (`API_SUFFIX_PRODUCTS_BASE`, `API_SUFFIX_PLATFORM_ENTITLEMENT`,
 * `API_SUFFIX_TENANT_PROVISIONING`, `API_SUFFIX_TENANTS`; follow-up to PR
 * #2105, which left them literal because the enum lives in shared ui-core
 * and editing it re-scopes the affected set across every app) — every
 * prefix below is now enum-derived, no bare-string entries remain.
 */
export const ADMIN_API_MODULE_PREFIXES: readonly string[] = [
  ApiLocalRoutesEnum.API_SUFFIX_ADMIN_DASHBOARD, // admin-dashboard-api
  ApiLocalRoutesEnum.API_SUFFIX_ADMIN_REGISTERATION, // admin-register-api
  ApiLocalRoutesEnum.API_SUFFIX_LEADERBOARD_ADMIN, // leaderboard-admin-api
  ApiLocalRoutesEnum.API_SUFFIX_LICENSE, // licensing-api
  ApiLocalRoutesEnum.API_SUFFIX_BODY_MEASUREMENTS, // manage-bodymeasurements-api
  ApiLocalRoutesEnum.API_SUFFIX_BOOKING, // manage-booking-api
  ApiLocalRoutesEnum.API_SUFFIX_CLUBS, // manage-clubs-api
  ApiLocalRoutesEnum.API_SUFFIX_CONTACT_REQUESTS, // contact-requests-api (admin inbox GETs)
  ApiLocalRoutesEnum.API_SUFFIX_CUSTOMERS, // manage-customers-api
  ApiLocalRoutesEnum.API_SUFFIX_DISCOUNTS, // manage-discounts-api
  ApiLocalRoutesEnum.API_SUFFIX_EMAILS, // manage-emails-api
  ApiLocalRoutesEnum.API_SUFFIX_TEST_EVENTS, // manage-events-api
  ApiLocalRoutesEnum.API_SUFFIX_OPERATORS, // manage-operators-api
  ApiLocalRoutesEnum.API_SUFFIX_ORDERS, // manage-orders-api
  ApiLocalRoutesEnum.API_SUFFIX_PAYMENTS, // manage-payments-api
  ApiLocalRoutesEnum.API_SUFFIX_PRODUCTS_BASE, // manage-products-api
  ApiLocalRoutesEnum.API_SUFFIX_REGIONS, // manage-regions-api
  ApiLocalRoutesEnum.API_SUFFIX_MANAGE_REPORTS_API, // manage-reports-api
  ApiLocalRoutesEnum.API_SUFFIX_RESERVATIONS, // manage-reservations-api
  ApiLocalRoutesEnum.API_SUFFIX_SIGNUP_SEARCH, // manage-signups-api
  ApiLocalRoutesEnum.API_SUFFIX_TRUCKS, // manage-trucks-api
  ApiLocalRoutesEnum.API_SUFFIX_PLATFORM_ENTITLEMENT, // platform-entitlement (only 'self' has a concrete sub-route)
  ApiLocalRoutesEnum.API_SUFFIX_TENANT, // tenant-api
  ApiLocalRoutesEnum.API_SUFFIX_TENANT_PROVISIONING, // tenant-provisioning
  ApiLocalRoutesEnum.API_SUFFIX_TENANTS, // tenants (branding controller: 'tenants/:tenantId/branding')
];

/**
 * `@Public()` sub-routes living UNDER the admin module prefixes above,
 * verified against their controllers. These must be `allowAnonymous` route
 * configs listed BEFORE the module wildcards: auth0-angular resolves a
 * request against the allowedList with first-match-wins semantics
 * (`findMatchingRoute` = `from(config.allowedList).pipe(first(...))`,
 * fesm2020 `AuthHttpInterceptor`), so an earlier `allowAnonymous` entry
 * shields the route from the later bare-string wildcard. `allowAnonymous`
 * still attaches a token opportunistically when a session exists — admin
 * behavior is unchanged — but a failed silent token fetch degrades to an
 * anonymous request instead of killing it (#2056 failure mode).
 *
 * `httpMethod` is set where the SAME path also has token-guarded sibling
 * methods, so only the public verb is shielded and the guarded verbs fall
 * through to the token-required wildcard.
 */
export function buildAdminPublicRouteEntries(api: string): HttpInterceptorRouteConfig[] {
  return [
    // GET manage-discounts-api/validate/:promoCode (manage-discounts-api.controller.ts).
    { uri: `${api}/${ApiLocalRoutesEnum.API_SUFFIX_DISCOUNTS_VALIDATE}/*`, allowAnonymous: true },
    // POST manage-payments-api/webhook — Stripe server-to-server webhook
    // (stripe-webhook.controller.ts); the SPA never calls it, listed so the
    // wildcard can never claim it.
    { uri: `${api}/${ApiLocalRoutesEnum.API_SUFFIX_PAYMENTS}/webhook`, allowAnonymous: true },
    // GET manage-products-api/stripe/products (products.controller.ts) —
    // exact match only; the guarded /stripe/products/:id falls to the wildcard.
    { uri: `${api}/${ApiLocalRoutesEnum.API_SUFFIX_PRODUCTS}`, allowAnonymous: true },
    // GET manage-regions-api (list, ADR-0006 Phase B deferred) — GET only;
    // POST base + details CRUD stay token-required.
    { uri: `${api}/${ApiLocalRoutesEnum.API_SUFFIX_REGIONS}`, httpMethod: 'GET', allowAnonymous: true },
    // POST manage-reservations-api/public — bring-my-truck lead capture.
    { uri: `${api}/${ApiLocalRoutesEnum.API_SUFFIX_RESERVATIONS_PUBLIC}`, allowAnonymous: true },
    // GET tenant-api (tenant list; method-level @Public, tenant-api.controller.ts)
    // — GET base only; details/users/admin-me stay token-required.
    { uri: `${api}/${ApiLocalRoutesEnum.API_SUFFIX_TENANT}`, httpMethod: 'GET', allowAnonymous: true },
    // GET tenants/:tenantId/branding (manage-branding-api.controller.ts) —
    // GET only; the guarded PUT falls through to the wildcard. The 'tenants'
    // prefix serves ONLY the branding controller today.
    { uri: `${api}/${ApiLocalRoutesEnum.API_SUFFIX_TENANTS}/*`, httpMethod: 'GET', allowAnonymous: true },
  ];
}

/**
 * Public carve-outs FIRST (first-match-wins), then each module prefix
 * expanded into the two forms auth0-angular's matcher needs: the exact base
 * (`${api}/<prefix>` — matched with `===` after query stripping; covers
 * bare-base calls like `POST tenant-provisioning` and
 * `GET manage-reservations-api`) and the wildcard (`${api}/<prefix>/*` —
 * matched with `startsWith`; covers every sub-path). A `<prefix>/*` entry
 * alone would NOT match the bare base.
 */
export function buildAdminAllowedList(api: string): (string | HttpInterceptorRouteConfig)[] {
  return [
    ...buildAdminPublicRouteEntries(api),
    ...ADMIN_API_MODULE_PREFIXES.flatMap(prefix => [`${api}/${prefix}`, `${api}/${prefix}/*`]),
  ];
}

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: ENV, useValue: environment },
    provideAnimations(),
    // Pre-auth theming (login screen). `environment.companyId` is the deployment's
    // tenant SLUG ('abx'), not a tenant id — `init()` with it hit
    // `/tenants/abx/branding`, which never has a row, so the admin portal always
    // booted with the defaults and ignored saved branding. AppComponent re-themes
    // with the signed-in admin's own tenant id once the role guard resolves it.
    provideAppInitializer(async () => {
      await inject(BrandingService).initBySlug(environment.companyId);
    }),
    provideAuth0({
      ...environment.auth0,
      httpInterceptor: {
        // #2061 — enumerated per-module list, see ADMIN_API_MODULE_PREFIXES.
        allowedList: buildAdminAllowedList(environment.api),
      },
    }),
    // withInterceptorsFromDi() lets DI-registered interceptors (TenantInterceptor,
    // registered below via HTTP_INTERCEPTORS) run alongside the functional
    // authHttpInterceptorFn. Without it the TenantInterceptor never fires, so the
    // x-tenant-id header is dropped and tenant-scoped admin reads return 403 (#1456).
    // ngZoneReentryInterceptor is registered FIRST (outermost) so it is the
    // last thing a response passes through before reaching app code — see
    // its doc comment / #1811 for why the auth token step can escape NgZone.
    provideHttpClient(
      withFetch(),
      // Embedded ROPG login (login redesign): embeddedAwareAuthHttpInterceptorFn
      // attaches an embedded session's bearer token itself and bypasses the SDK
      // interceptor; with no embedded session it delegates to the stock
      // authHttpInterceptorFn + allowedList above unchanged.
      withInterceptors([ngZoneReentryInterceptor, embeddedAwareAuthHttpInterceptorFn, httpErrorInterceptor]),
      withInterceptorsFromDi(),
    ),
    // Embedded ROPG login: bridge AuthService so isAuthenticated$/user$/logout()
    // (and AuthGuard/AdminRoleGuard, which inject AuthService) see an embedded
    // session; stock behaviour when the embeddedLoginEnabled flag is off.
    { provide: AuthService, useClass: EmbeddedBridgedAuthService },
    // App-wide ErrorHandler — surfaces uncaught runtime errors as an accessible
    // toast instead of a silent half-rendered screen (no provider existed before).
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    // Initializes the Sentry SDK (#2191) — a no-op unless SENTRY_ENABLED_FRONTEND
    // + SENTRY_DSN_FRONTEND are set; GlobalErrorHandler.reportToSink() is the call site.
    provideSentryErrorReporting(environment),
    // #2296 — without this the router keeps the outgoing page's scroll offset,
    // so a new page can land mid-scroll (e.g. Members detail → Orders lands at
    // the pagination footer). Quick-links (club-details) scroll in place via a
    // click handler (a bare `<a href="#...">` resolves against `<base href="/">` and
    // leaves the page), so anchorScrolling is left at its default and this only
    // changes push/back-forward scroll behavior.
    provideRouter(
      withMaintenanceMode(appRoutes, environment.maintenanceMode),
      withComponentInputBinding(),
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled' }),
    ),
    provideStore(
      {},
      {
        runtimeChecks: {
          strictStateImmutability: true,
          strictActionImmutability: true,
        },
      },
    ),
    // TenantInterceptor is registered ONLY here (#2499). The admin portal is the
    // one app whose requests are scoped by a client-attached `X-Tenant-ID`
    // header (see tenantInterceptor.service.ts for the interceptor's own
    // failure-mode documentation). The other three apps deliberately do not
    // register it — each scopes requests differently; see the comment at the
    // HttpClient provider in their own app.config.ts.
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TenantInterceptor,
      multi: true,
    },
    provideRouterStore(),
    // NgRx DevTools leak full action/state history to the Redux DevTools extension.
    // Only register them in non-production builds.
    ...(isDevMode() ? [provideStoreDevtools({ maxAge: 25 })] : []),
    {
      provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
      useValue: {
        showDelay: 0,
        hideDelay: 0,
        touchendHideDelay: 1500,
      },
    },
    importProvidersFrom(
      MatTooltipModule,
      MatDialogModule,
      NgxEchartsModule.forRoot({
        echarts: () => import('echarts'),
      }),
      ToastrModule.forRoot({
        positionClass: 'toast-top-right',
        timeOut: 3000,
        closeButton: true,
        progressBar: true,
        preventDuplicates: true,
        newestOnTop: true,
      }),
    ),
  ],
};
